/**
 * @file scrollMainTo.ts
 * @brief Scroll the admin shell `<main>` container to an in-page target.
 *
 * @details
 * `scrollIntoView` aligns to the browser viewport, which breaks when the
 * app shell uses a nested `overflow-y-auto` main scroller with fixed
 * header/footer chrome. Always scroll via the marked main root instead.
 * Pass `alignBelowId` when the reading line is a transformed sticky strip
 * (MLR format bar) — `offsetHeight` ignores CSS transforms (08-09-2026).
 *
 * @author DES
 * @date 06-07-2026
 */

export const MAIN_SCROLL_ROOT_ATTR = "data-scroll-root";

export function getMainScrollRoot(): HTMLElement | null {
  return document.querySelector<HTMLElement>(`main[${MAIN_SCROLL_ROOT_ATTR}]`);
}

function getStickySubnavOffset(): number {
  const bar = document.querySelector<HTMLElement>("[data-sticky-subnav]");
  return bar?.offsetHeight ?? 0;
}

/**
 * Viewport Y to sit the target under. `alignBelowId` uses the element's
 * *visible* bottom (`getBoundingClientRect`, so CSS transforms count) —
 * `offsetHeight` on `[data-sticky-subnav]` is wrong when the MLR chrome
 * translates into the shell topbar slot (08-09-2026).
 */
export function getMainAlignLineY(alignBelowId?: string, gap = 8): number {
  if (alignBelowId) {
    const rail = document.getElementById(alignBelowId);
    if (rail) return rail.getBoundingClientRect().bottom + gap;
  }
  const bar = document.querySelector<HTMLElement>("[data-sticky-subnav]");
  if (bar) return bar.getBoundingClientRect().bottom + gap;
  const root = getMainScrollRoot();
  return (root?.getBoundingClientRect().top ?? 0) + 96;
}

export function scrollMainToSection(
  sectionId: string,
  options?: {
    behavior?: ScrollBehavior;
    offset?: number;
    /** Align the target just below this element's visible bottom. */
    alignBelowId?: string;
  },
): boolean {
  const main = getMainScrollRoot();
  const target = document.getElementById(sectionId);
  if (!main || !target) return false;

  const behavior = options?.behavior ?? "smooth";
  const nextTop = options?.alignBelowId
    ? Math.max(
        0,
        main.scrollTop +
          target.getBoundingClientRect().top -
          getMainAlignLineY(options.alignBelowId, options.offset ?? 8),
      )
    : Math.max(
        0,
        main.scrollTop +
          (target.getBoundingClientRect().top - main.getBoundingClientRect().top) -
          ((options?.offset ?? 16) + getStickySubnavOffset()),
      );

  main.scrollTo({ top: nextTop, behavior });
  return true;
}

export function scrollMainToTop(
  options?: { behavior?: ScrollBehavior },
): boolean {
  const main = getMainScrollRoot();
  if (!main) return false;
  main.scrollTo({ top: 0, behavior: options?.behavior ?? "smooth" });
  return true;
}
